/**
 * ConfirmActionDialog — one generic AlertDialog wrapper for every
 * "are you sure?" confirmation in the app.
 *
 * Why this exists:
 *   Before this component, ad-hoc <AlertDialog>+<AlertDialogContent>+...
 *   blocks were copy/pasted across ItineraryView (duplicate segment),
 *   EditSegmentDialog (delete payment), ActiveSessionsPanel (sign out
 *   other devices), TripSharingPage (revoke share), etc. Each one
 *   reinvented the title / description / cancel-vs-action button color
 *   matrix slightly differently, and busy-state handling was bespoke.
 *
 * Single source of truth keeps the UX consistent:
 *   - keyboard focus, ARIA labels, and z-index from one Radix slot
 *   - visual variants pick the right brand color for the action
 *   - in-flight `loading` state disables both buttons + swaps the label
 *
 * Props:
 *   open            — controlled open state
 *   onOpenChange    — Radix onOpenChange callback
 *   title           — heading copy (string or node)
 *   description     — body copy (string or node). Optional.
 *   confirmLabel    — button label, default "Confirm"
 *   cancelLabel     — cancel label, default "Cancel"
 *   busyLabel       — label while `loading`. Default "Working…"
 *   variant         — visual style for the confirm button:
 *                     "danger" (red, default for destructive actions)
 *                     "primary" (sky)
 *                     "indigo" (used by duplicate-segment)
 *                     "amber"  (warnings)
 *   loading         — disables both buttons + shows busyLabel
 *   onConfirm       — async or sync confirm handler. Errors are NOT
 *                     swallowed — caller is expected to handle them.
 *   testIdPrefix    — used to derive testids:
 *                       `${prefix}-dialog`, `-cancel`, `-confirm`
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const VARIANT_CLASS = {
  danger: "bg-red-600 hover:bg-red-700",
  primary: "bg-sky-500 hover:bg-sky-600",
  indigo: "bg-indigo-600 hover:bg-indigo-700",
  amber: "bg-amber-500 hover:bg-amber-600",
};

const ConfirmActionDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busyLabel = "Working…",
  variant = "danger",
  loading = false,
  onConfirm,
  testIdPrefix = "confirm-action",
}) => {
  const confirmClass = VARIANT_CLASS[variant] || VARIANT_CLASS.danger;

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (loading) return;
    await onConfirm?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !loading && onOpenChange?.(o)}>
      <AlertDialogContent data-testid={`${testIdPrefix}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={loading}
            data-testid={`${testIdPrefix}-cancel`}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className={confirmClass}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {loading ? busyLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmActionDialog;
